import { cn } from "@playerelite/ui";
import { RETENTION_DAYS, RetentionCell, RetentionRow } from "./analytics-utils";

/** Retention above this reads as the darkest cell. */
const SCALE_MAX = 45;

const cellStyle = (value: RetentionCell) =>
  value === null
    ? undefined
    : {
        backgroundColor: `rgb(var(--pe-action) / ${(
          0.06 +
          0.5 * (Math.min(value, SCALE_MAX) / SCALE_MAX)
        ).toFixed(2)})`,
      };

const Cell = ({ value, strong }: { value: RetentionCell; strong?: boolean }) => (
  <td className="p-0.5">
    <div
      className={cn(
        "flex h-8 items-center justify-center rounded text-fg",
        value === null && "bg-surface-sunken",
        strong && "font-semibold"
      )}
      style={cellStyle(value)}
    >
      {value === null ? <span className="sr-only">Not reached yet</span> : `${value}%`}
    </div>
  </td>
);

const DAY_HEADERS = Array.from({ length: RETENTION_DAYS }, (_, i) => `Day ${i + 1}`);

type Props = {
  rows: RetentionRow[];
  averages: RetentionCell[];
  averageSignUps: number;
};

const RetentionHeatmap = ({ rows, averages, averageSignUps }: Props) => (
  <div className="flex flex-col gap-3">
    <div className="flex items-center gap-2 self-end text-xs text-fg-muted" aria-hidden="true">
      <span>0%</span>
      <span
        className="h-2.5 w-28 rounded-full"
        style={{
          background: "linear-gradient(90deg, rgb(var(--pe-action) / 0.06), rgb(var(--pe-action) / 0.56))",
        }}
      />
      <span>{SCALE_MAX}%+</span>
    </div>
    {/* Scrolls inside the card at narrow widths; the padding keeps focus rings unclipped. */}
    <div className="-m-1 overflow-x-auto p-1">
      <table className="w-full min-w-[56rem] border-separate border-spacing-0 font-mono text-xs tabular-nums">
        <caption className="sr-only">
          Percent of each day's new members who opened the app again on each following day
        </caption>
        <thead>
          <tr className="text-fg-muted">
            <th scope="col" className="px-1 py-2 text-left font-medium">
              Sign-up day
            </th>
            <th scope="col" className="px-2 py-2 text-right font-medium">
              New
            </th>
            {DAY_HEADERS.map((d) => (
              <th key={d} scope="col" className="px-1 py-2 text-center font-medium">
                {d}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row" className="px-1 text-left font-semibold text-fg">
              Average
            </th>
            <td className="px-2 text-right font-semibold text-fg">{averageSignUps}</td>
            {averages.map((v, i) => (
              <Cell key={i} value={v} strong />
            ))}
          </tr>
          <tr aria-hidden="true">
            <td colSpan={RETENTION_DAYS + 2} className="h-2" />
          </tr>
          {rows.map((row) => (
            <tr key={row.dateKey}>
              <th scope="row" className="whitespace-nowrap px-1 text-left font-normal text-fg-muted">
                {row.dateKey}
              </th>
              <td className="px-2 text-right text-fg-muted">{row.signUpCount}</td>
              {row.cells.map((v, i) => (
                <Cell key={i} value={v} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export default RetentionHeatmap;
